/**
 * ShareDialog Component
 *
 * Modal dialog for sharing resources with copy-to-clipboard functionality
 * and email sharing for SPs/FAs.
 *
 * Features:
 * - Tab interface with "Copy Link" and "Send via Email" options
 * - Copy link to clipboard with success feedback
 * - Email sharing via ResourceShareModal (for SPs/FAs with clients)
 * - Conditional rendering based on user role and client availability
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { ResourceShareModal } from './ResourceShareModal';

type ShareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareUrl: string;
  resourceId: string;
  resourceTitle: string;
};

export function ShareDialog({
  open,
  onOpenChange,
  shareUrl,
  resourceId,
  resourceTitle
}: ShareDialogProps) {
  const { user } = useAuth();
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('copy-link');
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user can share via email (SP or FA)
  const canShareViaEmail = user?.role === 'SERVICE_PROVIDER' || user?.role === 'FINANCIAL_ADVISOR';

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);

      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      // Set new timeout and store the ID
      copyTimeoutRef.current = setTimeout(() => {
        setCopySuccess(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleOpenShareModal = () => {
    setShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
  };

  // Reset copy success when dialog closes
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Clear timeout when dialog closes
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
      setCopySuccess(false);
      setActiveTab('copy-link');
    }
    onOpenChange(open);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Share this resource</DialogTitle>
            <DialogDescription>
              {canShareViaEmail
                ? 'Copy the link or send this resource to your clients via email.'
                : 'Copy the link below to share this resource with others.'}
            </DialogDescription>
          </DialogHeader>

          {canShareViaEmail ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="copy-link">Copy Link</TabsTrigger>
                <TabsTrigger value="send-email">Send via Email</TabsTrigger>
              </TabsList>

              <TabsContent value="copy-link" className="space-y-4 mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                  <Button onClick={handleCopyLink} disabled={copySuccess}>
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                {copySuccess && (
                  <p className="text-sm text-green-600 flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                      ✓
                    </span>
                    Link copied to clipboard!
                  </p>
                )}
              </TabsContent>

              <TabsContent value="send-email" className="space-y-4 mt-4">
                <div className="text-sm text-gray-600 mb-4">
                  Select care seekers from your client list to share this resource via email.
                </div>
                <Button
                  onClick={handleOpenShareModal}
                  className="w-full"
                >
                  Select Recipients
                </Button>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                />
                <Button onClick={handleCopyLink} disabled={copySuccess}>
                  {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
              </div>

              {copySuccess && (
                <p className="text-sm text-green-600 flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                    ✓
                  </span>
                  Link copied to clipboard!
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resource Share Modal */}
      {canShareViaEmail && (
        <ResourceShareModal
          open={shareModalOpen}
          onOpenChange={handleCloseShareModal}
          resourceId={resourceId}
          resourceTitle={resourceTitle}
        />
      )}
    </>
  );
}
